// @flow
import React from 'react';
import classNames from 'classnames/bind';
import { Container, Row, Col } from 'Components/Grid';
import { Watch } from 'scrollmonitor-react';
import { formatPrice, stripTags } from 'Utils';
import Price from 'Components/Price';
import OfferModel from 'Stores/OfferModel';
import styles from './styles.scss';
import { getButtonLabel } from './getButtonLabel';
import CallToAction from 'Components/CallToAction';
import ConsumptionData from 'Components/ConsumptionData';
import Typography from 'Components/Typography';
import RichText from 'Components/RichText';

type Props = {
  id: string,
  offer: OfferModel,
  isInViewport: boolean,
  stopWatcher: () => void,
  onOpenContact: () => void,
  background: string,
  hasForm: String,
};

type State = {};

const hLevelMap = { h1: 'h2', h2: 'h3', h3: 'h4', h4: 'h5', h5: 'h6', h6: 'h6' };

class OfferEntryPrivate extends React.Component<Props, State> {
  // @see https://github.com/facebook/react/issues/12670
  state = {};

  static getDerivedStateFromProps(props: Props) {
    if (props.isInViewport) {
      props.stopWatcher();
    }

    return null;
  }

  pricePrefix(value: string, parentLevel: string = 'h3') {
    return (
      <Typography.Headline
        className={styles.priceTitle}
        forceUppercase={true}
        level={hLevelMap[parentLevel]}
        value={value}
      />
    );
  }

  get cashPricePrefix() {
    const { offer } = this.props;
    let text = offer.offerSubtype === 'VOLUME' ? 'Für' : 'Ab';

    if (offer.cashPrice && offer.cashPrice.pricePrefix) {
      text = offer.cashPrice.pricePrefix;
    }

    return this.pricePrefix(text, offer.headline.level);
  }

  get rightPricePrefix() {
    const { offer } = this.props;
    let text = offer.offerSubtype === 'VOLUME' ? 'Leasing für' : 'Leasing ab';

    if (offer.rightPrice && offer.rightPrice.pricePrefix) {
      text = offer.rightPrice.pricePrefix;
    }

    return this.pricePrefix(text, offer.headline.level);
  }

  get leftPricePrefix() {
    const { offer } = this.props;
    let text = offer.offerSubtype === 'VOLUME' ? 'Finanzierung für' : 'Finanzierung ab';

    if (offer.leftPrice && offer.leftPrice.pricePrefix) {
      text = offer.leftPrice.pricePrefix;
    }

    return this.pricePrefix(text, offer.headline.level);
  }

  render() {
    const { offer, isInViewport, id, hasForm } = this.props;

    const className = classNames(styles.entry, {
      [styles.private]: true,
      [styles.isInViewport]: window.isPreview || isInViewport,
    });

    return (
      <div className={className} id={id}>
        <Container>
          {offer.headline && (
            <Row>
              <Col>
                <Typography.Headline
                  {...offer.headline}
                  levelVisually={'h3'}
                  className={styles.title}
                  additionalAriaLabelText={
                    offer.consumptionInfoDescription
                      ? stripTags(offer.consumptionInfoDescription)
                      : undefined
                  }
                />
              </Col>
            </Row>
          )}
          {offer.cashPrice && offer.cashPrice.price > 0 ? (
            <Row>
              <Col>
                <div className={styles.cashPrice} data-e2e="offer-cashPrice">
                  {this.cashPricePrefix}
                  {formatPrice(offer.cashPrice.price)}{' '}
                  <span
                    data-e2e="offer-cashPrice-priceText"
                    dangerouslySetInnerHTML={{ __html: offer.cashPrice.priceText }}
                  />
                </div>
              </Col>
            </Row>
          ) : null}
          <Row className={styles.imageRow}>
            <Col cols={12} m={6} e2e="offer-consumptionInfoDescription">
              {offer.consumptionInfoDescription && (
                <ConsumptionData
                  consumptionText={[offer.consumptionInfoDescription]}
                  inside="offer"
                  hideCloseButton
                  ariaHidden={true}
                />
              )}
              <RichText e2e="offer-description" className={styles.text} content={offer.bodyText} />
            </Col>
            <Col cols={12} m={6}>
              <div
                className={styles.image}
                style={
                  isInViewport || window.isPreview
                    ? { backgroundImage: `url(${offer.image.src}) ` }
                    : undefined
                }
              />
            </Col>
          </Row>
          <Row align="center" className={styles.imageRow}>
            <Col cols={12} m={6}>
              {offer.offerSubtype === 'VOLUME' &&
              offer.customerBenefitPrice &&
              offer.customerBenefitPrice > 0 ? (
                <span className={styles.customerBenefit} data-e2e="offer-customerBenefit">
                  {offer.customerBenefitPriceText && (
                    <span
                      data-e2e="offer-customerBenefitPriceText"
                      dangerouslySetInnerHTML={{ __html: `${offer.customerBenefitPriceText} ` }}
                    ></span>
                  )}
                  {formatPrice(offer.customerBenefitPrice)}
                  {offer.customerBenefitPriceFootnote && (
                    <span
                      data-e2e="offer-customerBenefitPriceFootnote"
                      dangerouslySetInnerHTML={{ __html: offer.customerBenefitPriceFootnote }}
                    ></span>
                  )}
                </span>
              ) : null}
            </Col>
          </Row>
          <Row align="center">
            {offer.leftPrice && offer.leftPrice.price > 0 && (
              <Col align="center" m={4}>
                <div className={styles.priceWrapper} data-e2e="leftPricePrefix">
                  {this.leftPricePrefix}
                  <Price value={formatPrice(offer.leftPrice.price)} />
                  <p
                    className={styles.priceText}
                    dangerouslySetInnerHTML={{ __html: offer.leftPrice.priceText }}
                  />
                </div>
              </Col>
            )}

            {offer.rightPrice &&
              offer.leftPrice &&
              offer.rightPrice.price > 0 &&
              offer.leftPrice.price > 0 && (
                <Col align="center" m={2}>
                  <div className={styles.hr} />
                </Col>
              )}

            {offer.rightPrice && offer.rightPrice.price > 0 && (
              <Col align="center" m={4}>
                <div className={styles.priceWrapper} data-e2e="rightPricePrefix">
                  {this.rightPricePrefix}
                  <Price value={formatPrice(offer.rightPrice.price)} />
                  <p
                    className={styles.priceText}
                    dangerouslySetInnerHTML={{ __html: offer.rightPrice.priceText }}
                  />
                </div>
              </Col>
            )}
          </Row>
          <Row align="center" className={styles.buttonRow}>
            <Col m={10} align="center">
              {offer.cta1 ? (
                <CallToAction {...offer.cta1} hasForm={offer.cta1.mazdaForm} />
              ) : (
                <CallToAction
                  additional={`/?showmodal=true&modaltype=REQUEST_FOR_PROPOSAL&carmodel=${offer.carlineDeeplink}`}
                  buttonVariant="primary"
                  href={`${window.location.pathname}?showmodal=true&modaltype=REQUEST_FOR_PROPOSAL&carmodel=${offer.carlineDeeplink}`}
                  label={getButtonLabel(hasForm)}
                />
              )}
            </Col>
            {offer.cta2 && (
              <Col m={10} align="center" className={styles.buttonSecondary}>
                <CallToAction
                  {...offer.cta2}
                  label={offer.cta2.label || 'Direkt verfügbare Modelle'}
                  hasForm={offer.cta2.mazdaForm}
                />
              </Col>
            )}
          </Row>
        </Container>
      </div>
    );
  }
}

export default Watch(OfferEntryPrivate);
